/**
 * The web app's platform layer: everything besides playing that talks to the api or
 * keeps player data. Screens import from here (`../platform/index.ts`).
 *
 * Contract for the screens (docs/WEB-PLATFORM.md has the full reference):
 * - boot: `initPlatform({ route })` once, then `readShareParams()` (and track
 *   `shared_link_opened` when `fromShare`);
 * - puzzles: `fetchDailyLevels`, `fetchDaily`, `fetchDailyByNumber`, `fetchPractice`,
 *   `fetchPracticePuzzle`;
 * - the Today screen: `getResumeInfo(dateKey, level)`, `loadStats()`, `currentStreak`;
 * - the Stats screen: `summarizeStats(loadStats(), todayKey)`;
 * - events: `track(name, props)`; sharing: `shareResult(text, meta)`.
 * Gameplay events and solve sync are wired inside `useGame` already.
 */
import { type DateKey, DEFAULT_LEVEL, type Level, localDateKey, type PlayerStats } from '@arrow/game-engine';

import { createPersistence, type Persistence, type ResumeInfo, slotFor } from '../storage/persistence.ts';
import { track } from './analytics.ts';
import { applyUrlFlags, isAnalyticsEnabled, platformStore } from './config.ts';
import { getDeviceId, recordVisit, registerDevice, type Visit } from './identity.ts';
import { retryPendingSolves, solveSync } from './sync.ts';

// --- Levels -------------------------------------------------------------------------
export { DEFAULT_LEVEL, isLevel, LEVELS, levelForDifficulty } from '@arrow/puzzle-schema';
export type { Level } from '@arrow/puzzle-schema';

// --- Puzzles (api) --------------------------------------------------------------------
export {
	ApiError,
	type DailyLevelSummary,
	type DailyLevels,
	type DailyPuzzle,
	fetchDaily,
	fetchDailyByNumber,
	fetchDailyLevels,
	fetchPractice,
	fetchPracticePuzzle,
	type LoadError,
	type PracticePuzzle,
	type PracticeSummary,
} from '../api/client.ts';

// --- Identity, analytics, solve sync ----------------------------------------------------
export { APP_VERSION, isAnalyticsEnabled, isInternalDevice, setAnalyticsEnabled, setInternalDevice } from './config.ts';
export { getDeviceId, recordVisit, registerDevice, type Visit } from './identity.ts';
export {
	type DailyProps,
	EVENT_NAMES,
	type EventName,
	type EventProps,
	flushAnalytics,
	type ShareOutcome,
	track,
} from './analytics.ts';
export { retryPendingSolves, type SolveStatus, type SolveUpload, syncSolve } from './sync.ts';

// --- Sharing --------------------------------------------------------------------------
export {
	challengeUrl,
	newShareId,
	readShareParams,
	SHARE_PARAMS,
	SHARE_URL,
	type ShareMeta,
	type ShareParams,
	type ShareTarget,
	shareResult,
	shareUrl,
} from '../game/share.ts';

// --- Stats & saved games ------------------------------------------------------------------
export {
	bestStreak,
	comparison,
	completedLevels,
	currentStreak,
	dayRecord,
	levelName,
	localDateKey,
	summarizeStats,
	type DateKey,
	type DayRecord,
	type PlayerStats,
	type SolveTotals,
	type StatsSummary,
} from '@arrow/game-engine';
export { createPersistence, type Persistence, type ResumeInfo, slotFor, type SlotSpec } from '../storage/persistence.ts';

/** The app's persistence (localStorage), shared by the platform helpers below. */
let shared: Persistence | null = null;
export function persistence(): Persistence {
	shared ??= createPersistence();
	return shared;
}

/**
 * Play / Continue / See result for the daily at (`dateKey`, `level`), from what's
 * saved on this device; no puzzle needed. Never throws.
 */
export function getResumeInfo(dateKey: DateKey, level: Level = DEFAULT_LEVEL): ResumeInfo {
	return persistence().resumeInfo(slotFor({ kind: 'daily', date: dateKey, level }));
}

/** Resume info for a practice puzzle. */
export function getPracticeResumeInfo(puzzleId: string): ResumeInfo {
	return persistence().resumeInfo(slotFor({ kind: 'practice', puzzleId }));
}

/** The player's stats (per level, migrated from older saves). Never throws. */
export function loadStats(): PlayerStats {
	return persistence().loadStats();
}

export interface InitOptions {
	/** The route the app opened on (e.g. `/`, `/daily/42`), for `app_opened`. */
	route: string;
	/** The player's local date (or the dev override). Default: today. */
	todayKey?: DateKey;
	/** Opened from the home screen or in a browser tab (`app_opened.display`). */
	display?: 'browser' | 'standalone';
}

export interface PlatformInfo extends Visit {
	deviceId: string;
	analytics: boolean;
}

let initialised: PlatformInfo | null = null;

/**
 * Boot the platform, once per page load (later calls return the first result):
 * applies `?noanalytics`, makes the device id, registers the device (fire-and-forget),
 * notes the visit, tracks `app_opened { route, isReturning, daysSinceFirstSeen, display }`,
 * starts background delivery and retries solves that didn't reach the api before.
 * Never throws and never waits on the network.
 */
export function initPlatform(options: InitOptions): PlatformInfo {
	if (initialised) return initialised;
	try {
		applyUrlFlags();
		const deviceId = getDeviceId(platformStore);
		const visit = recordVisit(options.todayKey ?? localDateKey(new Date()), platformStore);
		const analytics = isAnalyticsEnabled(platformStore);
		initialised = { deviceId, analytics, ...visit };
		registerDevice(platformStore);
		track('app_opened', {
			route: options.route.slice(0, 200),
			isReturning: visit.isReturning,
			daysSinceFirstSeen: visit.daysSinceFirstSeen,
			...(options.display ? { display: options.display } : {}),
		});
		if (analytics) {
			solveSync.start();
			void retryPendingSolves();
		}
		return initialised;
	} catch {
		const today = options.todayKey ?? localDateKey(new Date());
		initialised = { deviceId: '', analytics: false, isReturning: false, daysSinceFirstSeen: 0, firstSeen: today };
		return initialised;
	}
}
